import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useGql } from '@ah/design';
import {
  NOTIFICATIONS_QUERY,
  NOTIFICATION_ADDED_SUB,
  UNREAD_COUNT_SUB,
} from '../shared/queries.ts';
import { navigateToUrl } from '../shared/navigateToUrl.ts';

export function NotificationsBell() {
  const gql = useGql();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    gql
      .request<{ unreadNotificationCount: number }>(NOTIFICATIONS_QUERY, { limit: 1 })
      .then((r) => setUnread(r.unreadNotificationCount))
      .catch(() => {});

    const unsubAdded = gql.subscribe<{
      onNotificationAdded: { id: string; title: string; body: string; targetUrl: string | null };
    }>(NOTIFICATION_ADDED_SUB, {}, (data) => {
      const n = data.onNotificationAdded;
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          const notif = new Notification(n.title, {
            body: n.body,
            icon: '/pwa-192.png',
            tag: `gt-${n.id}`,
          });
          if (n.targetUrl) {
            notif.onclick = (ev) => {
              ev.preventDefault();
              window.focus();
              navigateToUrl(navigate, n.targetUrl!);
              notif.close();
            };
          }
        } catch {
          // Safari can throw if the page is not visible; ignore.
        }
      }
    });

    const unsubUnread = gql.subscribe<{ onUnreadNotificationCountChanged: number }>(
      UNREAD_COUNT_SUB,
      {},
      (d) => setUnread(d.onUnreadNotificationCountChanged),
    );

    return () => {
      unsubAdded();
      unsubUnread();
    };
  }, [gql, navigate]);

  return (
    <button
      type="button"
      onClick={() => navigate('/notifications')}
      aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
      className="relative flex items-center justify-center w-9 h-9 rounded-lg text-foreground hover:bg-accent"
    >
      <Bell size={20} />
      {unread > 0 && (
        <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center leading-none">
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </button>
  );
}
